<template>
    <div class="channel-selection-step">
        <div class="channel-panel">
            <div class="section-title">
                <div>
                    <span class="eyebrow">Основной канал</span>
                    <h2>Выберите, откуда будут уходить сообщения</h2>
                </div>
                <span class="badge">Шаг 1</span>
            </div>

            <div class="channel-grid">
                <button
                    v-for="channel in channels"
                    :key="channel.type"
                    type="button"
                    class="channel"
                    :class="{ active: selected === channel.type }"
                    @click="select(channel.type)"
                >
                    <span class="icon-wrap">
                        <img :src="channel.logo" :alt="channel.label" class="channel-logo" />
                    </span>
                    <span class="channel-label">{{ channel.label }}</span>
                </button>
            </div>
        </div>

        <div class="channel-offer">
            <div>
                <strong>Подключить ещё один канал</strong>
                <span>
                    Резервный канал можно добавить после основного подключения, чтобы включить
                    каскадную отправку.
                </span>
            </div>
        </div>

        <div v-if="!useExternalFooter" class="actions">
            <button
                type="button"
                class="next-btn"
                :disabled="!selected"
                @click="confirm"
            >
                Далее
            </button>
        </div>
    </div>
</template>

<script>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { CHANNEL_TYPES } from '@/vars/general'

export default {
    name: 'ChannelSelectionStep',
    emits: ['confirm'],
    props: {
        /**
         * true: «Далее» в футере общей карточки онбординга (YC и other CRM)
         * false: «Далее» внутри шага (если шаг вне wizardNav)
         */
        useExternalFooter: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const selected = ref(null)
        const wizardNav = inject('onboardingWizardNav', null)

        const waLogo = require('@/assets/img/messenger-logo/wa-logo.png')
        const tgLogo = require('@/assets/img/messenger-logo/telegram-logo.png')
        const maxLogo = require('@/assets/img/messenger-logo/max-logo.png')

        const channels = computed(() => [
            {
                type: CHANNEL_TYPES.whatsappChannel,
                label: 'WhatsApp',
                logo: waLogo,
            },
            {
                type: CHANNEL_TYPES.telegramChannel,
                label: 'Telegram',
                logo: tgLogo,
            },
            {
                type: CHANNEL_TYPES.maxChannel,
                label: 'MAX',
                logo: maxLogo,
            },
        ])

        const select = (type) => {
            selected.value = type
        }

        const confirm = () => {
            if (!selected.value) return
            emit('confirm', selected.value)
        }

        const syncWizardNav = () => {
            if (!props.useExternalFooter || !wizardNav) return
            wizardNav.set({
                canBack: false,
                canNext: Boolean(selected.value),
                showNext: true,
                nextLoading: false,
                nextLabel: 'Далее',
                footerOffer: null,
                onBack: null,
                onNext: confirm,
            })
        }

        watch(selected, syncWizardNav)
        watch(
            () => props.useExternalFooter,
            syncWizardNav
        )

        onMounted(() => {
            syncWizardNav()
        })

        onBeforeUnmount(() => {
            if (props.useExternalFooter) {
                wizardNav?.reset?.()
            }
        })

        return {
            selected,
            channels,
            select,
            confirm,
            CHANNEL_TYPES,
        }
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.channel-selection-step
    width: 100%
    display: grid
    gap: 16px

.channel-panel
    width: 100%
    max-width: 100%
    min-width: 0
    border: 1px solid rgba(229, 232, 242, 0.94)
    border-radius: 24px
    background: #fff
    box-shadow: 0 16px 42px rgba(26, 26, 46, 0.07)
    padding: 28px
    box-sizing: border-box

.section-title
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 16px
    margin-bottom: 24px

    h2
        font-size: 24px
        letter-spacing: -0.045em
        line-height: 1.12
        margin: 0
        font-weight: 800
        color: $dark-new !important

.eyebrow
    display: inline-flex
    align-items: center
    gap: 8px
    color: $main-new !important
    font-size: 11px
    font-weight: 900
    text-transform: uppercase
    letter-spacing: 0.12em
    margin-bottom: 10px

.badge
    display: inline-flex
    align-items: center
    gap: 6px
    height: 28px
    border-radius: 999px
    padding: 0 10px
    background: $bcg-card-new
    color: $main-new !important
    font-size: 11px
    font-weight: 800
    white-space: nowrap
    flex: 0 0 auto

.channel-grid
    display: grid
    grid-template-columns: repeat(3, minmax(0, 1fr))
    gap: 14px

.channel
    height: 124px
    border: 1.5px solid #D7DCEB
    background: #fff
    border-radius: 18px
    padding: 16px
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 12px
    text-transform: uppercase
    color: $dark-new
    font-size: 12px
    font-weight: 900
    letter-spacing: 0.04em
    transition: 0.18s ease
    cursor: pointer

    &:hover
        border-color: rgba(82, 90, 213, 0.55)
        box-shadow: 0 10px 28px rgba(82, 90, 213, 0.10)
        transform: translateY(-1px)

    &.active
        border-color: $main-new
        box-shadow: 0 0 0 4px rgba(82, 90, 213, 0.10), 0 16px 34px rgba(82, 90, 213, 0.16)

.icon-wrap
    width: 48px
    height: 48px
    border-radius: 16px
    display: grid
    place-items: center
    flex: 0 0 auto
    background: #F8FAFF
    overflow: hidden

.channel-logo
    width: 30px
    height: 30px
    object-fit: contain
    display: block

.channel-label
    color: inherit !important
    text-align: center
    line-height: 1.2

.channel-offer
    display: grid
    grid-template-columns: 1fr
    align-items: center
    gap: 16px
    padding: 20px 22px
    border-radius: 20px
    background: linear-gradient(135deg, #101629 0%, #29336D 100%)
    border: 0
    color: #fff
    box-shadow: 0 16px 36px rgba(26, 26, 46, 0.11)

    strong
        display: block
        font-size: 16px
        letter-spacing: -0.025em
        margin-bottom: 4px
        color: #fff !important

    span
        display: block
        color: rgba(255, 255, 255, 0.70) !important
        font-size: 13px
        line-height: 1.45

.actions
    display: flex
    justify-content: flex-end

.next-btn
    height: 42px
    border: 0
    border-radius: 12px
    padding: 0 18px
    font-size: 13px
    font-weight: 900
    display: inline-flex
    align-items: center
    justify-content: center
    cursor: pointer
    background: $main-new
    color: #fff
    box-shadow: 0 10px 20px rgba(82, 90, 213, 0.24)

    &:disabled
        cursor: not-allowed
        opacity: 0.48

@media (max-width: 900px)
    .channel-grid
        grid-template-columns: repeat(2, minmax(0, 1fr))
    .channel
        height: 110px

@media (max-width: 640px)
    .channel-panel
        padding: 20px
        border-radius: 20px
    .section-title
        flex-direction: column
        align-items: flex-start
    .section-title h2
        font-size: 20px
    .channel-grid
        grid-template-columns: 1fr
    .channel
        height: 86px
        flex-direction: row
        justify-content: flex-start
    .actions
        .next-btn
            width: 100%
</style>
